'use client';

import type { ReactNode } from 'react';
import {
  useLearningSource,
  type LearningItem,
  type LearningRow,
} from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';
import { SectionShell } from '../_shared/SectionShell';
import { SkeletonRow } from '../_shared/SkeletonRow';
import { EmptyState } from '../_shared/EmptyState';
import { StaleIndicator } from '../_shared/StaleIndicator';
import { AdapterPlaceholder } from '../_shared/AdapterPlaceholder';
import { prettify } from '../_shared/prettify';
import { type PaddingBox } from '../_shared/resolvePadding';

/**
 * Shared internal frame for every LMS rail backed by
 * {@code LearningSource.fetchRow(source, limit)}. Handles the
 * loading / empty / stale / error / placeholder states uniformly so
 * each individual rail block reduces to "pick source, pick limit,
 * pick item renderer".
 *
 * <p>Used by {@link CourseRow}, {@link CampsAndCompetitions},
 * {@link Top10Row}, {@link AutoScrollMarquee}.
 */
export interface LearningRailFrameProps {
  blockType: string;
  source: string;
  limit: number;
  /** When provided, overrides the server-supplied label + the
   *  prettified-source fallback. */
  title?: string;
  emptyTitle?: string;
  emptyHint?: string;
  subtitle?: string;
  seeAllLabel?: string;
  seeAllHref?: string;
  /** Skeleton layout. Default {@code rail}. */
  skeletonLayout?: 'rail' | 'grid' | 'list';
  /** Space-token keys for the outer <section> padding. */
  paddingOuter?: PaddingBox;
  /** Space-token keys for the inner content-area padding. */
  paddingInner?: PaddingBox;
  /** Renderer for the loaded items. */
  children: (items: LearningItem[], row: LearningRow) => ReactNode;
}

export function LearningRailFrame({
  blockType,
  source,
  limit,
  title,
  emptyTitle,
  emptyHint,
  subtitle: _subtitle,
  seeAllLabel,
  seeAllHref,
  skeletonLayout = 'rail',
  paddingOuter,
  paddingInner,
  children,
}: LearningRailFrameProps) {
  const learning = useLearningSource();

  const state = useAsyncResource<LearningRow>(
    async (signal) => {
      if (!learning) return null;
      return await learning.fetchRow({ source, limit, signal });
    },
    [learning, source, limit],
  );

  const authorTitle = typeof title === 'string' && title.trim() ? title.trim() : undefined;
  const serverLabel = state.kind === 'loaded' ? state.data.label : undefined;
  const label = authorTitle || serverLabel || prettify(source);

  const resolvedSeeAllHref =
    typeof seeAllHref === 'string' && seeAllHref.trim() ? seeAllHref : undefined;
  const resolvedSeeAllLabel = (typeof seeAllLabel === 'string' && seeAllLabel.trim()) || 'See all';
  const action: ReactNode = resolvedSeeAllHref ? (
    <a
      href={resolvedSeeAllHref}
      style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--color-text-brand, var(--color-brand-600, #3b5bdb))',
        textDecoration: 'none',
      }}
    >
      {resolvedSeeAllLabel} →
    </a>
  ) : undefined;

  const titleNode: ReactNode =
    state.kind === 'loaded' && state.data.stale ? (
      <>
        {label}
        <span style={{ marginLeft: 12, display: 'inline-block', verticalAlign: 'middle' }}>
          <StaleIndicator />
        </span>
      </>
    ) : (
      label
    );

  if (!learning) {
    return (
      <SectionShell
        title={label}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <AdapterPlaceholder blockType={blockType} adapterName="LearningSource" />
      </SectionShell>
    );
  }

  if (state.kind === 'loading') {
    return (
      <SectionShell
        title={titleNode}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <SkeletonRow count={limit} layout={skeletonLayout} />
      </SectionShell>
    );
  }

  const items: LearningItem[] = state.kind === 'loaded' ? state.data.items : [];

  if (items.length === 0) {
    return (
      <SectionShell
        title={titleNode}
        action={action}
        paddingOuter={paddingOuter}
        paddingInner={paddingInner}
      >
        <EmptyState
          title={emptyTitle ?? 'Nothing to show right now'}
          hint={emptyHint ?? 'Try switching to a different source.'}
          diagnostic={source}
        />
      </SectionShell>
    );
  }

  const row = state.kind === 'loaded' ? state.data : { source, label, total: items.length, items };
  return (
    <SectionShell
      title={titleNode}
      action={action}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      {children(items, row)}
    </SectionShell>
  );
}
